import './assets/main.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config';
import Aura from '@primeuix/themes/aura';
import {Amplify} from "aws-amplify";
import awsConfig from "@/aws-exports";
import { createI18n } from 'vue-i18n'

Amplify.configure(awsConfig)
import App from './App.vue'
import router from './router'
import * as Sentry from "@sentry/vue";
const app = createApp(App)
import 'primeicons/primeicons.css'
import ToastService from 'primevue/toastservice';

import {FloatLabel, InputText, Button, Message, Password, InputGroup, InputGroupAddon, Divider, Popover} from "primevue";
import {definePreset} from "@primeuix/themes";


app.component('FloatLabel', FloatLabel);
app.component('InputText', InputText);
app.component('Button', Button);
app.component('Message', Message);
app.component('Password', Password);
app.component('InputGroup', InputGroup);
app.component('InputGroupAddon', InputGroupAddon);
app.component('Divider', Divider);
app.component('Popover', Popover);

const Noir = definePreset(Aura, {
    semantic: {
        primary: {
            50: '{surface.50}',
            100: '{surface.100}',
            200: '{surface.200}',
            300: '{surface.300}',
            400: '{surface.400}',
            500: '{surface.500}',
            600: '{surface.600}',
            700: '{surface.700}',
            800: '{surface.800}',
            900: '{surface.900}',
            950: '{surface.950}'
        },
        colorScheme: {
            light: {
                primary: {
                    color: '{primary.950}',
                    contrastColor: '#ffffff',
                    hoverColor: '{primary.800}',
                    activeColor: '{primary.700}'
                },
                highlight: {
                    background: '{primary.950}',
                    focusBackground: '{primary.700}',
                    color: '#ffffff',
                    focusColor: '#ffffff'
                }
            },
            dark: {
                primary: {
                    color: '{primary.50}',
                    contrastColor: '{primary.950}',
                    hoverColor: '{primary.200}',
                    activeColor: '{primary.300}'
                },
                highlight: {
                    background: '{primary.50}',
                    focusBackground: '{primary.300}',
                    color: '{primary.950}',
                    focusColor: '{primary.950}'
                }
            }
        }
    }
});
app.use(PrimeVue, {
    theme: {
        preset: Noir,
        options: {
            darkModeSelector: "",
        }
    }
});


const availableLocales = import.meta.env.VITE_SUPPORTED_LOCALES.split(",");

const detectBrowserLanguage = () => {
    const language = window.navigator.language;
    const lang = availableLocales.includes(language)
        ? language
        : import.meta.env.VITE_DEFAULT_LOCALE;
    localStorage.setItem("locale", lang);
    return lang;
};

import en from "./locale/en.json";
import es from "./locale/es.json";
type MessageSchema = typeof en

const i18n = createI18n<[MessageSchema], 'en' | 'es'>({
    locale: localStorage.getItem("locale")
        ? `${localStorage.getItem("locale")}`
        : detectBrowserLanguage(),
    fallbackLocale: "en",
    legacy: false,
    globalInjection: true,
    messages: {en, es}
})

Sentry.init({
    app,
    dsn: "https://fa67c1c9f5ff86760a36655c5f62b68b@o4509355188944896.ingest.us.sentry.io/4509389124665344",

    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration()
    ],
    // Tracing
    tracesSampleRate: 1.0, // Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
        /^https:\/\/auth-api\.bono\.eco/,
        /^https:\/\/auth-api\.test\.bono\.eco/,
        /^https:\/\/cognito-idp\.us-west-1\.amazonaws\.com/,
        /^http:\/\/localhost(?::\d+)?/
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0,// If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    environment: import.meta.env.VITE_APP_STAGE, // dev, test, prod
    enabled: ['test', 'prod'].includes(import.meta.env.VITE_APP_STAGE),
});

app.use(i18n);
app.use(createPinia());
app.use(ToastService);
app.use(router);

app.mount('#app');
